import { useEffect, useLayoutEffect } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import {
  getWindowScrollPosition,
  scrollWindowTo,
  scrollWindowToTop,
} from '../../utils/scroll.js';

// A SPA route change keeps the current scroll offset, so clicking a link low on
// a page lands the user mid-way down the next one. We reset the offset for
// PUSH/REPLACE. The document scrolls through <body> in this app, which browsers
// do not restore consistently, so POP positions are saved and restored here.

const scrollPositions = new Map();

function scrollToHash(hash, attempt = 0) {
  const id = decodeURIComponent(hash.slice(1));
  const el = id ? document.getElementById(id) : null;

  if (el) {
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    return;
  }

  // Lazy routes mount after the location changes, so the target may not exist
  // yet — retry for a few frames before giving up and going to the top.
  if (attempt < 10) {
    requestAnimationFrame(() => scrollToHash(hash, attempt + 1));
  } else {
    scrollWindowToTop();
  }
}

export default function ScrollToTop() {
  const location = useLocation();
  const { pathname, hash, key } = location;
  const navigationType = useNavigationType();

  useEffect(() => {
    const previous = window.history.scrollRestoration;
    window.history.scrollRestoration = 'manual';
    return () => {
      window.history.scrollRestoration = previous;
    };
  }, []);

  // A layout-effect cleanup runs while the page being left still has its DOM,
  // allowing us to capture the exact position associated with that history key.
  useLayoutEffect(() => {
    return () => {
      scrollPositions.set(key, getWindowScrollPosition());
    };
  }, [key]);

  useEffect(() => {
    if (navigationType === 'POP') {
      const saved = scrollPositions.get(key);
      if (!saved) return;

      let cancelled = false;
      const timers = [];
      const restore = () => {
        if (!cancelled) scrollWindowTo(saved.x, saved.y);
      };

      // Data-backed pages can grow after their first render. Retry while they
      // settle so the requested offset is not clamped by a loading skeleton.
      [0, 50, 150, 350, 700, 1200].forEach((delay) => {
        timers.push(window.setTimeout(restore, delay));
      });

      const cancel = () => {
        cancelled = true;
        timers.forEach(window.clearTimeout);
      };
      window.addEventListener('wheel', cancel, { once: true, passive: true });
      window.addEventListener('touchstart', cancel, { once: true, passive: true });
      window.addEventListener('pointerdown', cancel, { once: true, passive: true });
      window.addEventListener('keydown', cancel, { once: true });

      return () => {
        cancel();
        window.removeEventListener('wheel', cancel);
        window.removeEventListener('touchstart', cancel);
        window.removeEventListener('pointerdown', cancel);
        window.removeEventListener('keydown', cancel);
      };
    }

    if (hash) {
      scrollToHash(hash);
      return;
    }

    scrollWindowToTop();
    // Lazy route chunks mount a frame later; re-assert once the new page is in
    // the DOM so a restored offset can't win the race.
    const raf = requestAnimationFrame(() => scrollWindowToTop());
    return () => cancelAnimationFrame(raf);
  }, [pathname, hash, navigationType]);

  return null;
}
